const PERSISTENCE_CART_ENDPOINT = '/apps/persistent-cart/';

class CartWatcher {
  constructor() {
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.isSyncingPersistenceCart = false;
  }

  init() {
    this.dispatchCartChanges()
      .then(() => {
        this.observeCartChanges();
      })
      .catch((error) => {
        console.error(error);
      });
    this.cartEvents();
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  async isVariantAvailable(variantId) {
    try {
      const res = await fetch(`/variants/${variantId}.json`);

      if (!res.ok) {
        return false;
      }

      const data = await res.json();

      return Boolean(data.product_variant);
    } catch {
      return false;
    }
  }

  async filterAvailableItems(items) {
    const validItems = {};

    for (const [variantId, quantity] of Object.entries(items)) {
      const isAvailable = await this.isVariantAvailable(variantId);

      if (isAvailable) {
        validItems[variantId] = quantity;
      } else {
        console.warn(`Variant ${variantId} not available`);
      }
    }

    return validItems;
  }

  setCart(cart) {
    localStorage.setItem('cart', JSON.stringify(cart));
  }

  getCart() {
    return JSON.parse(localStorage.getItem('cart')) || { items: [] };
  }

  setCartCount(count) {
    document.querySelectorAll('.cart-count-bubble, [data-cart-count]').forEach((bubble) => {
      const visibleCount = bubble.querySelector('span[aria-hidden="true"]');
      const hiddenCount = bubble.querySelector('.visually-hidden');

      if (visibleCount) { visibleCount.textContent = count; }
      if (hiddenCount) { hiddenCount.textContent = count; }

      bubble.classList.toggle('hidden', count === 0);
    });
  }

  findChangedItems(oldCart, newCart) {
    const oldItems = oldCart.items || [];
    const newItems = newCart.items || [];
    const oldMap = new Map(oldItems.map((item) => [item.variant_id, item]));
    const newMap = new Map(newItems.map((item) => [item.variant_id, item]));
    const added = [];
    const removed = [];

    for (const [variantId, newItem] of newMap.entries()) {
      const oldItem = oldMap.get(variantId);

      if (!oldItem) {
        added.push({ ...newItem });
      } else if (newItem.quantity > oldItem.quantity) {
        added.push({ ...newItem });
      } else if (newItem.quantity < oldItem.quantity) {
        removed.push({ ...newItem });
      }
    }

    for (const [variantId, oldItem] of oldMap.entries()) {
      if (!newMap.has(variantId)) {
        removed.push({ ...oldItem, quantity: 0 });
      }
    }

    return { added, removed };
  }

  async dispatchCartChanges() {
    if (this.isSyncingPersistenceCart) { return; }
    const oldCart = this.getCart();
    const newCart = await this.fetchCart();
    const changes = this.findChangedItems(oldCart, newCart);
    changes.cart = newCart;
    const event = new CustomEvent('persistence-cart:updated', { detail: changes });
    document.dispatchEvent(event);

    this.setCart(newCart);
  }

  observeCartChanges() {
    const cartObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const isValidRequestType = ['xmlhttprequest', 'fetch'].includes(entry.initiatorType);
        const isCartChangeRequest = /\/cart\//.test(entry.name);
        if (isValidRequestType && isCartChangeRequest) {
          this.dispatchCartChanges();
        }
      });
    });
    cartObserver.observe({ entryTypes: ['resource'] });
  }

  async clearCart() {
    try {
      this.isSyncingPersistenceCart = true;
      const response = await fetch('/cart/clear.js', { method: 'POST' });
      const textResponse = await response.text();
      const customerCart = JSON.parse(textResponse);

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'persistence-cart',
        cartData: customerCart,
      });

      if (this.cart && !this.cart.classList.contains('is-empty')) {
        this.cart.classList.add('is-empty');
      }

      this.setCartCount(customerCart.item_count);
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        this.isSyncingPersistenceCart = false;
      }, 500);
    }
  }

  async replaceCart(items) {
    try {
      this.isSyncingPersistenceCart = true;
      await fetch('/cart/clear.js', { method: 'POST' });

      const validItems = await this.filterAvailableItems(items);
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: validItems }),
      });

      const textResponse = await response.text();
      const customerCart = JSON.parse(textResponse);

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'persistence-cart',
        cartData: customerCart,
      });

      if (this.cart && this.cart.classList.contains('is-empty')) {
        this.cart.classList.remove('is-empty');
      }

      this.setCartCount(customerCart.item_count);
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        this.isSyncingPersistenceCart = false;
      }, 500);
    }
  }

  /**
   * Update persistence cart and then update actual cart with the response
   * @param body
   * @returns {Promise<void>}
   */
  async fetchPersistenceCart(body) {
    try {
      this.isSyncingPersistenceCart = true;
      const response = await fetch(PERSISTENCE_CART_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const textResponse = await response.text();
      const customerCart = JSON.parse(textResponse);
      const validItems = await this.filterAvailableItems(customerCart);

      const cart = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: validItems }),
      });
      const cartUpdateTextResponse = await cart.text();
      const customerCartUpdate = JSON.parse(cartUpdateTextResponse);

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'persistence-cart',
        cartData: customerCartUpdate,
      });

      if (this.cart && this.cart.classList.contains('is-empty')) {
        this.cart.classList.remove('is-empty');
      }

      this.setCartCount(customerCartUpdate.item_count);
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        this.isSyncingPersistenceCart = false;
      }, 500);
    }
  }

  cartEvents() {
    if (localStorage.getItem('loggedIn') && window.customerCart && Object.keys(window.customerCart).length > 0) {
      this.replaceCart(window.customerCart);
    } else if (localStorage.getItem('loggedIn')) {
      this.clearCart();
    }

    document.addEventListener('persistence-cart:updated', (event) => {
      const fetchBody = {
        variantsToIncrementQty: [],
        variantsToUpdate: [],
      };

      if (event.detail.added.length > 0) {
        event.detail.added.forEach((item) => {
          if (item.variant_id && item.quantity && item.final_price > 0) {
            fetchBody.variantsToUpdate.push({
              variantId: `${item.variant_id}`,
              qty: item.quantity,
            });
          }
        });
      }
      if (event.detail.removed.length > 0) {
        event.detail.removed.forEach((item) => {
          if (item.variant_id) {
            fetchBody.variantsToUpdate.push({
              variantId: `${item.variant_id}`,
              qty: item.quantity || 0,
            });
          }
        });
      }

      if (fetchBody.variantsToIncrementQty.length > 0 || fetchBody.variantsToUpdate.length > 0) {
        this.fetchPersistenceCart(fetchBody)
          .catch((error) => {
            console.error(error);
          });
      }
    });

    document.addEventListener('customer:loggedIn', () => {
      const fetchBody = {
        variantsToIncrementQty: [],
      };

      const actualCart = this.getCart();
      actualCart.items.forEach((item) => {
        if (item.variant_id && item.quantity && item.final_price > 0) {
          fetchBody.variantsToIncrementQty.push({
            variantId: `${item.variant_id}`,
            qty: item.quantity,
          });
        }
      });

      this.fetchPersistenceCart(fetchBody)
        .catch((error) => {
          console.error(error);
        });
    });

    document.addEventListener('customer:loggedOut', () => {
      this.clearCart();
    });
  }
}

const myCartWatcher = new CartWatcher();
myCartWatcher.init();

